import React, { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { X, BookOpen, RefreshCw, CheckCircle2 } from 'lucide-react';
import { moduleApi } from '../../lib/api';

/**
 * Connexion du tableau avec le Livre de réservations. Tout est réglable ici, par tableau : quels services du livre alimentent le
 * tableau, quels statuts sont repris, ce qu'on fait du prépaiement, ce qu'on retire, et si la synchronisation est automatique.
 * Le livre fait foi pour l'identité, l'heure, les couverts et la table ; le tableau fait foi pour l'argent.
 */

type Reglages = {
    actif: boolean; auto: boolean; service_ids: string[]; statuts: string[];
    seulement_prepayees: boolean; prepaiement_en_cb: boolean; retirer_annulees: boolean;
};
type Etat = {
    livre_actif: boolean; reglages: Reglages; statuts_proposes: { id: string; label: string }[];
    services: { id: string; nom: string; heure_debut: string; heure_fin: string }[];
    apercu: { dans_le_livre: number; deja_reprises: number; couverts: number } | null;
};
type Resultat = { ajoutees: number; mises_a_jour: number; retirees: number; conservees: number; total: number };

function Interrupteur({ label, aide, valeur, onChange, disabled }: { label: string; aide?: string; valeur: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
    return (
        <label className={`flex items-start gap-3 py-2.5 ${disabled ? 'opacity-50' : 'cursor-pointer'}`}>
            <input type="checkbox" role="switch" checked={valeur} disabled={disabled} onChange={(e) => onChange(e.target.checked)} className="mt-1 h-4 w-4 accent-brand-600" />
            <span className="min-w-0">
                <span className="block text-sm font-bold text-content">{label}</span>
                {aide && <span className="block text-xs text-content-muted leading-snug">{aide}</span>}
            </span>
        </label>
    );
}

export function LivreLienModal({ boardId, onClose, onSynced }: { boardId: number; onClose: () => void; onSynced: () => void }) {
    const [etat, setEtat] = useState<Etat | null>(null);
    const [reglages, setReglages] = useState<Reglages | null>(null);
    const [erreur, setErreur] = useState('');
    const [envoi, setEnvoi] = useState(false);
    const [resultat, setResultat] = useState<Resultat | null>(null);

    useEffect(() => {
        moduleApi.getExceptionServiceLivre(boardId)
            .then((e: Etat) => { setEtat(e); setReglages(e.reglages); })
            .catch((e: any) => setErreur(e?.message || 'Chargement impossible'));
    }, [boardId]);

    const maj = (p: Partial<Reglages>) => setReglages((r) => (r ? { ...r, ...p } : r));
    const basculer = (liste: string[], id: string) => (liste.includes(id) ? liste.filter((x) => x !== id) : [...liste, id]);

    const enregistrer = async (): Promise<boolean> => {
        if (!reglages) return false;
        try {
            const r: any = await moduleApi.saveExceptionServiceLivre(boardId, reglages);
            setEtat((e) => (e ? { ...e, reglages: r.reglages, apercu: r.apercu } : e));
            return true;
        } catch (e: any) { setErreur(e?.message || 'Enregistrement impossible'); return false; }
    };

    const synchroniser = async () => {
        setEnvoi(true); setErreur(''); setResultat(null);
        try {
            if (!(await enregistrer())) return;
            const r: Resultat = await moduleApi.syncExceptionServiceLivre(boardId);
            setResultat(r);
            onSynced();
            const e: Etat = await moduleApi.getExceptionServiceLivre(boardId);
            setEtat(e);
        } catch (e: any) { setErreur(e?.message || 'Synchronisation impossible'); }
        finally { setEnvoi(false); }
    };

    const fermerEtEnregistrer = async () => { if (reglages && JSON.stringify(reglages) !== JSON.stringify(etat?.reglages)) await enregistrer(); onClose(); };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
                role="dialog" aria-modal="true" aria-label="Livre de réservations"
                className="bg-white dark:bg-surface border border-border rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
                <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                    <h3 className="text-lg font-bold text-content flex items-center gap-2"><BookOpen size={18} className="text-brand-500" /> Livre de réservations</h3>
                    <button onClick={fermerEtEnregistrer} aria-label="Fermer" className="text-content-muted hover:text-content transition-colors"><X size={20} /></button>
                </div>

                <div className="p-6 space-y-4">
                    {!etat && !erreur && <p className="text-sm text-content-muted">Chargement…</p>}
                    {erreur && <p role="alert" className="text-sm font-semibold text-red-500">{erreur}</p>}

                    {etat && !etat.livre_actif && (
                        <p className="text-sm text-content-muted">Le Livre de réservations n'est pas activé pour votre établissement. Demandez son activation depuis « Mes modules » pour reprendre ses réservations ici.</p>
                    )}

                    {etat && reglages && etat.livre_actif && (
                        <>
                            <p className="text-sm text-content-muted">
                                Reprenez dans ce tableau les réservations du livre du même jour, sans les ressaisir. Le livre fait foi pour le nom, l'heure, les couverts et la table ; vos encaissements ici ne sont jamais écrasés.
                            </p>

                            <div className="rounded-xl bg-surface-soft border border-border px-4 py-3 text-sm text-content">
                                {etat.apercu
                                    ? <><b>{etat.apercu.dans_le_livre}</b> réservation{etat.apercu.dans_le_livre > 1 ? 's' : ''} dans le livre ce jour-là selon vos réglages ({etat.apercu.couverts} couvert{etat.apercu.couverts > 1 ? 's' : ''}), dont <b>{etat.apercu.deja_reprises}</b> déjà reprise{etat.apercu.deja_reprises > 1 ? 's' : ''} ici.</>
                                    : 'Aucun aperçu disponible.'}
                            </div>

                            <div>
                                <div className="text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5">Services du livre repris</div>
                                {etat.services.length === 0
                                    ? <p className="text-xs text-content-muted">Aucun service dans le livre pour l'instant.</p>
                                    : (
                                        <div className="space-y-1">
                                            <label className="flex items-center gap-2 text-sm text-content cursor-pointer">
                                                <input type="checkbox" checked={reglages.service_ids.length === 0} onChange={() => maj({ service_ids: [] })} className="h-4 w-4 accent-brand-600" />
                                                Tous les services du jour
                                            </label>
                                            {etat.services.map((s) => (
                                                <label key={s.id} className="flex items-center gap-2 text-sm text-content cursor-pointer">
                                                    <input type="checkbox" checked={reglages.service_ids.includes(s.id)} onChange={() => maj({ service_ids: basculer(reglages.service_ids, s.id) })} className="h-4 w-4 accent-brand-600" />
                                                    {s.nom} <span className="text-xs text-content-muted">{s.heure_debut}–{s.heure_fin}</span>
                                                </label>
                                            ))}
                                        </div>
                                    )}
                            </div>

                            <div>
                                <div className="text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5">Statuts repris</div>
                                <div className="flex flex-wrap gap-2">
                                    {etat.statuts_proposes.map((s) => {
                                        const on = reglages.statuts.includes(s.id);
                                        return (
                                            <button key={s.id} type="button" aria-pressed={on}
                                                onClick={() => maj({ statuts: basculer(reglages.statuts, s.id) })}
                                                className={`px-3 py-1.5 rounded-full border text-xs font-bold transition-all ${on ? 'border-brand-500 bg-brand-500/10 text-brand-600 dark:text-brand-400' : 'border-border text-content-muted hover:text-content'}`}>
                                                {s.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="divide-y divide-border">
                                <Interrupteur label="Seulement les réservations prépayées" aide="Les autres restent dans le livre, hors de ce tableau." valeur={reglages.seulement_prepayees} onChange={(v) => maj({ seulement_prepayees: v })} />
                                <Interrupteur label="Reporter le prépaiement en carte bancaire" aide="Le montant prépayé (moins les remboursements) arrive dans la colonne CB." valeur={reglages.prepaiement_en_cb} onChange={(v) => maj({ prepaiement_en_cb: v })} />
                                <Interrupteur label="Retirer les réservations annulées ou déplacées" aide="Une ligne qui a déjà des encaissements n'est jamais retirée." valeur={reglages.retirer_annulees} onChange={(v) => maj({ retirer_annulees: v })} />
                                <Interrupteur label="Synchroniser à chaque ouverture du tableau" aide="Les nouvelles réservations du livre arrivent seules. Sinon, utilisez le bouton ci-dessous." valeur={reglages.auto} onChange={(v) => maj({ auto: v, actif: v ? true : reglages.actif })} />
                            </div>

                            {resultat && (
                                <div role="status" className="flex items-start gap-2 rounded-xl bg-emerald-500/10 border border-emerald-500/30 px-4 py-3 text-sm text-content">
                                    <CheckCircle2 size={18} className="text-emerald-500 shrink-0 mt-0.5" />
                                    <span>
                                        {resultat.ajoutees} ajoutée{resultat.ajoutees > 1 ? 's' : ''}, {resultat.mises_a_jour} mise{resultat.mises_a_jour > 1 ? 's' : ''} à jour, {resultat.retirees} retirée{resultat.retirees > 1 ? 's' : ''}
                                        {resultat.conservees > 0 && <>, {resultat.conservees} gardée{resultat.conservees > 1 ? 's' : ''} car déjà encaissée{resultat.conservees > 1 ? 's' : ''}</>}.
                                    </span>
                                </div>
                            )}

                            <div className="flex items-center gap-2 pt-1">
                                <button onClick={synchroniser} disabled={envoi}
                                    className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold disabled:opacity-50 transition-all">
                                    <RefreshCw size={16} className={envoi ? 'animate-spin' : ''} /> {envoi ? 'Synchronisation…' : 'Enregistrer et synchroniser maintenant'}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </motion.div>
        </div>
    );
}
